import { useEffect, useState } from "react";
import {
    resolveCategoryId,
    fetchPublicFinalists,
    fetchMyVote,
    castVote,
    type PublicFinalist,
} from "../api/tcg";
import Turnstile from "./turnstile.component";
import { useLanguage } from "../i18n/language.context";
import { apiErrorKey } from "../i18n/errorMessages";

function FinalistVoting({
    categorySlug,
    onVoted,
}: {
    categorySlug: string;
    // Wird nach erfolgreicher Stimmabgabe mit dem Namen des gewählten
    // Finalisten aufgerufen. Die Seite blendet dann den Bestätigungs-
    // Abschnitt ein – analog zum "done"-Schritt der Nominierung.
    onVoted?: (displayText: string) => void;
}) {
    const { t: tr } = useLanguage();
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [finalists, setFinalists] = useState<PublicFinalist[]>([]);
    const [selected, setSelected] = useState<number | null>(null);
    const [myVote, setMyVote] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [justSaved, setJustSaved] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaReset, setCaptchaReset] = useState(0);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const id = await resolveCategoryId(categorySlug);
                if (cancelled) return;
                setCategoryId(id);
                const [fin, mine] = await Promise.all([fetchPublicFinalists(id), fetchMyVote(id)]);
                if (cancelled) return;
                setFinalists(fin);
                setMyVote(mine.finalistId);
                setSelected(mine.finalistId);
            } catch (err) {
                if (!cancelled) setError(tr(apiErrorKey(err)));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [categorySlug, tr]);

    async function handleVote() {
        if (categoryId === null || selected === null || !captchaToken) return;
        setSubmitting(true);
        setError(null);
        setJustSaved(false);
        try {
            await castVote(categoryId, selected, captchaToken);
            setMyVote(selected);
            setCaptchaToken(null);
            setCaptchaReset((n) => n + 1);

            const chosen = finalists.find((f) => f.id === selected);
            if (onVoted) {
                onVoted(chosen?.display_text ?? "");
            } else {
                // Ohne Callback bleibt das bisherige Inline-Feedback bestehen.
                setJustSaved(true);
            }
        } catch (err) {
            setError(tr(apiErrorKey(err)));
            setCaptchaToken(null);
            setCaptchaReset((n) => n + 1);
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) return <p className="voting-detail-placeholder">{tr("ui.common.loading")}</p>;

    if (finalists.length === 0) {
        return <p className="voting-detail-placeholder">{tr("ui.vote.noFinalists")}</p>;
    }

    return (
        <>
            {myVote !== null && (
                <p className="voting-already-voted">{tr("ui.vote.alreadyVoted")}</p>
            )}

            <div className="voting-finalist-list">
                {finalists.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        className={`voting-finalist-card${selected === f.id ? " selected" : ""}`}
                        onClick={() => { setSelected(f.id); setJustSaved(false); }}
                    >
                        <span className="voting-finalist-name">{f.display_text}</span>
                        {myVote === f.id && <span className="voting-finalist-badge">{tr("ui.vote.yourVote")}</span>}
                    </button>
                ))}
            </div>

            {error && <p className="voting-error">{error}</p>}
            {justSaved && (
                <p className="voting-vote-confirm">{tr("ui.vote.saved")}</p>
            )}

            <div className="voting-detail-section">
                <Turnstile
                    onToken={setCaptchaToken}
                    onExpire={() => setCaptchaToken(null)}
                    resetSignal={captchaReset}
                />
            </div>

            <button
                type="button"
                className="voting-primary-btn"
                disabled={selected === null || submitting || selected === myVote || !captchaToken}
                onClick={handleVote}
            >
                {submitting ? tr("ui.vote.saving") : myVote !== null ? tr("ui.vote.change") : tr("ui.vote.cast")}
            </button>
        </>
    );
}

export default FinalistVoting;